import React, { useState, useEffect, useRef } from 'react';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { CreditCard, AlertCircle, Loader2 } from 'lucide-react';
import subscriptionService from '../../services/subscriptionService';
import useStripeCheckout from '../../hooks/useStripeCheckout';
import StripeCheckoutStatus from '../shared/StripeCheckoutStatus';
import { isNetworkFailure } from '../../services/stripeConfigService';
import {
  isMissingPaymentIntentError,
  missingPaymentIntentHelpMessage,
} from '../../utils/stripeEnv';

// IMPORTANT:
// Don't initialize/load Stripe while offline, otherwise `stripe.js` fails to load and triggers an
// ugly "Uncaught runtime errors" overlay. We create `stripePromise` only when online.

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#000',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': {
        color: '#9ca3af',
      },
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
  hidePostalCode: true,
};

const buildSafeMetadata = (metadata = {}) => {
  const safeEntries = Object.entries(metadata).filter(([, value]) => {
    const valueType = typeof value;
    return valueType !== 'function' && value !== undefined;
  });
  return Object.fromEntries(safeEntries);
};

const PaymentForm = ({
  subscriptionId,
  billingInterval = 'monthly',
  amount,
  clientSecret,
  intentContext,
  onSuccess,
  onError,
  metadata = {},
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [cardholderName, setCardholderName] = useState(metadata?.cardholderName || '');

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      return;
    }

    if (!cardholderName || cardholderName.trim().length < 2) {
      setError('Please enter the cardholder name');
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: cardholderName,
        },
      });

      if (pmError) {
        setError(pmError.message);
        setProcessing(false);
        onError?.(pmError.message);
        return;
      }

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: paymentMethod.id,
        }
      );

      if (confirmError) {
        const message = isMissingPaymentIntentError(confirmError.message)
          ? missingPaymentIntentHelpMessage
          : confirmError.message;
        setError(message);
        setProcessing(false);
        onError?.(message);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        const confirmResponse = await subscriptionService.confirmSubscriptionPayment({
          subscription_id: subscriptionId,
          payment_intent_id: paymentIntent.id,
          payment_method_id: paymentMethod.id,
          cardholder_name: cardholderName,
          billing_interval: billingInterval,
          metadata: {
            ...buildSafeMetadata(metadata),
            stripe_customer_id: intentContext?.stripe_customer_id || undefined,
            stripe_subscription_id: intentContext?.stripe_subscription_id || undefined,
          }
        });

        if (confirmResponse.success) {
          onSuccess?.(confirmResponse.data);
        } else {
          const msg = confirmResponse.message || 'Payment confirmation failed';
          setError(msg);
          setProcessing(false);
          onError?.(msg);
        }
      } else {
        const msg = 'Payment was not successful. Please try again.';
        setError(msg);
        setProcessing(false);
        onError?.(msg);
      }
    } catch (err) {
      const raw = err.response?.data?.message || err.message || 'An error occurred during payment';
      const errorMessage = isMissingPaymentIntentError(raw)
        ? missingPaymentIntentHelpMessage
        : raw;
      setError(errorMessage);
      setProcessing(false);
      onError?.(errorMessage);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div>
        <label htmlFor="cardholder-name" className="block text-sm font-medium text-gray-700 mb-1.5">
          Cardholder Name
        </label>
        <input
          id="cardholder-name"
          type="text"
          value={cardholderName}
          onChange={(e) => {
            setCardholderName(e.target.value);
            metadata?.onCardholderNameChange?.(e.target.value);
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none text-sm"
          placeholder="John Doe"
          required
          disabled={processing}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Card Details
        </label>
        <div className="px-3 py-3 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-black focus-within:border-black">
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      <div className="bg-surface-muted rounded-lg p-4 border border-gray-200">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700">Total Amount</span>
          <span className="text-lg font-bold text-black">${Number(amount).toFixed(2)}</span>
        </div>
      </div>

      <button
        type="submit"
        disabled={processing || !stripe || !cardholderName}
        className="w-full py-3 px-4 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {processing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Processing Payment...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5 mr-2" />
            Pay ${Number(amount).toFixed(2)}
          </>
        )}
      </button>

      <p className="text-xs text-gray-500 text-center">
        Your payment is secured by Stripe. We never store your card details.
      </p>
    </form>
  );
};

const StripeSubscriptionPaymentForm = ({
  subscriptionId,
  billingInterval = 'monthly',
  amount,
  onSuccess,
  onError,
  metadata = {},
}) => {
  const stripeCheckout = useStripeCheckout();
  const { isOnline, isChecking, stripePromise, stripeReady, loading, error, retry, mode } =
    stripeCheckout;
  const [paymentSession, setPaymentSession] = useState(null);
  const [initError, setInitError] = useState(null);
  const [loadingIntent, setLoadingIntent] = useState(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const retryAll = () => {
    setPaymentSession(null);
    setInitError(null);
    retry();
  };

  useEffect(() => {
    let cancelled = false;
    setPaymentSession(null);
    setInitError(null);

    if (!isOnline || !stripeReady || !subscriptionId || !(amount > 0)) {
      return undefined;
    }

    const createPaymentIntent = async () => {
      setLoadingIntent(true);
      try {
        const response = await subscriptionService.createSubscriptionPaymentIntent({
          subscription_id: subscriptionId,
          billing_interval: billingInterval,
          metadata: buildSafeMetadata(metadata),
        });

        if (cancelled) return;

        if (!response.success || !response.data?.client_secret) {
          const msg = response.message || 'Failed to initialize payment';
          setInitError(msg);
          onErrorRef.current?.(msg);
          return;
        }

        if (mode && response.data.stripe_mode && mode !== response.data.stripe_mode) {
          const msg = 'Stripe configuration changed. Please close and reopen checkout.';
          setInitError(msg);
          onErrorRef.current?.(msg);
          return;
        }

        setPaymentSession({
          clientSecret: response.data.client_secret,
          stripe_customer_id: response.data.stripe_customer_id || null,
          stripe_subscription_id: response.data.stripe_subscription_id || null,
        });
      } catch (err) {
        if (cancelled) return;
        const errorMessage = isNetworkFailure(err)
          ? 'Could not reach the server. Check your connection and try again.'
          : err.response?.data?.message || err.message || 'Failed to create payment intent';
        setInitError(errorMessage);
        onErrorRef.current?.(errorMessage);
      } finally {
        if (!cancelled) {
          setLoadingIntent(false);
        }
      }
    };

    createPaymentIntent();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- metadata/onError omitted to avoid intent refetch storms
  }, [subscriptionId, amount, billingInterval, isOnline, stripeReady, mode]);

  const gate = (
    <StripeCheckoutStatus
      isOnline={isOnline}
      isChecking={isChecking}
      loading={loading}
      error={error}
      onRetry={retryAll}
      loadingMessage="Loading payment settings..."
    />
  );

  if (!isOnline || loading || error) {
    return gate;
  }

  if (initError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-600">{initError}</p>
            <button
              type="button"
              onClick={retryAll}
              className="mt-3 text-sm font-medium text-red-700 underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loadingIntent || !paymentSession?.clientSecret) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400 mr-2" />
        <span className="text-gray-600">Initializing payment...</span>
      </div>
    );
  }

  return (
    <Elements
      key={paymentSession.clientSecret}
      stripe={stripePromise}
    >
      <PaymentForm
        subscriptionId={subscriptionId}
        billingInterval={billingInterval}
        amount={amount}
        clientSecret={paymentSession.clientSecret}
        intentContext={paymentSession}
        onSuccess={onSuccess}
        onError={onError}
        metadata={metadata}
      />
    </Elements>
  );
};

export default StripeSubscriptionPaymentForm;
